/**
 * BriefPublic — public shareable view of a saved MVP brief at /brief/{id}.
 *
 * Mirrors OpportunityPublicView's pattern: anonymous fetch via GET /api/briefs/{id},
 * populates OG/Twitter meta tags for rich social previews, renders the full brief
 * (positioning, target persona, 3 features-with-evidence, pricing, GTM, ad headline),
 * and includes share buttons + CTA back into the app.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const _setMeta = (selector, key, value) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [attr, attrVal] = selector.replace(/^meta\[/, "").replace(/\]$/, "").split("=");
    el.setAttribute(attr, attrVal.replace(/['"]/g, ""));
    document.head.appendChild(el);
  }
  el.setAttribute(key, value);
};

export const BriefPublicView = ({ briefId, onEnterApp }) => {
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/briefs/${briefId}`, { timeout: 10000 })
      .then((r) => setBrief(r.data))
      .catch(() => setError(true));
  }, [briefId]);

  // Inject OG / Twitter meta tags so Twitter, LinkedIn, Slack show a rich card.
  useEffect(() => {
    if (!brief?.brief) return;
    const pageUrl = `${window.location.origin}/brief/${briefId}`;
    const ogImage = `${API}/briefs/${briefId}/og.png`;
    const title = `Prime Pillar Brief — alternative to ${brief.product_name}`;
    const desc = brief.brief.first_ad_headline || brief.brief.positioning || "";
    document.title = title;
    _setMeta('meta[name="description"]', "content", desc);
    _setMeta('meta[property="og:title"]', "content", title);
    _setMeta('meta[property="og:description"]', "content", desc);
    _setMeta('meta[property="og:type"]', "content", "article");
    _setMeta('meta[property="og:url"]', "content", pageUrl);
    _setMeta('meta[property="og:image"]', "content", ogImage);
    _setMeta('meta[property="og:image:width"]', "content", "1200");
    _setMeta('meta[property="og:image:height"]', "content", "630");
    _setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    _setMeta('meta[name="twitter:title"]', "content", title);
    _setMeta('meta[name="twitter:description"]', "content", desc);
    _setMeta('meta[name="twitter:image"]', "content", ogImage);
  }, [brief, briefId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-white to-indigo-50">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Brief not found</h1>
          <p className="text-zinc-600 mb-6">This brief may have been deleted or the link is incorrect.</p>
          <button
            onClick={onEnterApp}
            className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-xl"
            data-testid="brief-not-found-enter"
          >
            Discover opportunities → Prime Pillar
          </button>
        </div>
      </div>
    );
  }

  if (!brief?.brief) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        <div className="animate-pulse">Loading brief…</div>
      </div>
    );
  }

  const b = brief.brief;
  const pageUrl = `${window.location.origin}/brief/${briefId}`;
  const tweetText = `${b.first_ad_headline || b.positioning} — full MVP brief:`;
  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(pageUrl)}`;
  const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(pageUrl); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-indigo-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Brand header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={onEnterApp}
            className="text-sm text-violet-700 hover:text-violet-900 font-semibold"
            data-testid="brief-public-back"
          >
            ← Prime Pillar
          </button>
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">⚡ MVP Brief</span>
        </div>

        {/* Headline card */}
        <div className="bg-white rounded-2xl shadow-xl ring-1 ring-zinc-200 p-6 sm:p-8 mb-6" data-testid="brief-public-card">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-600 mb-2">
            An alternative to {brief.product_name}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 leading-tight mb-4">
            {b.positioning}
          </h1>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-base font-semibold text-zinc-900">
            "{b.first_ad_headline}"
          </div>
        </div>

        {/* Target persona */}
        <div className="bg-white rounded-2xl ring-1 ring-zinc-200 p-5 mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Target persona</div>
          <p className="text-zinc-800">{b.target_persona}</p>
        </div>

        {/* Features */}
        <div className="bg-white rounded-2xl ring-1 ring-zinc-200 p-5 mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Top 3 must-have features</div>
          <ol className="space-y-3">
            {(b.top_3_features || []).map((f, i) => (
              <li key={i} className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <span className="font-mono text-[10px] text-violet-600 mt-0.5">{i + 1}</span>
                  <span className="text-zinc-900">{f}</span>
                </div>
                {b.feature_evidence?.[i] && (
                  <blockquote className="mt-2 text-[12px] text-zinc-600 border-l-2 border-violet-300 pl-2 italic">
                    evidence: "{b.feature_evidence[i]}"
                  </blockquote>
                )}
              </li>
            ))}
          </ol>
        </div>

        {/* Pricing + GTM */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-2xl ring-1 ring-zinc-200 p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Pricing</div>
            <div className="text-zinc-900 text-lg font-semibold">{b.suggested_pricing}</div>
          </div>
          <div className="bg-white rounded-2xl ring-1 ring-zinc-200 p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">GTM angle</div>
            <div className="text-zinc-800 italic text-sm">"{b.gtm_angle}"</div>
          </div>
        </div>

        {brief.g2_url && (
          <a href={brief.g2_url} target="_blank" rel="noopener noreferrer"
             className="block text-center text-[11px] font-mono uppercase tracking-[0.15em] text-violet-700 hover:underline mb-6">
            Read the raw complaints on G2 ↗
          </a>
        )}

        {/* Share row */}
        <div className="bg-zinc-900 rounded-2xl p-5 sm:p-6 text-white mb-6" data-testid="brief-public-share">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2">Share this brief</div>
          <div className="flex flex-wrap items-center gap-2">
            <a href={tweetUrl} target="_blank" rel="noopener noreferrer"
               className="px-4 py-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold"
               data-testid="brief-public-tweet">🐦 Tweet this brief</a>
            <a href={liUrl} target="_blank" rel="noopener noreferrer"
               className="px-4 py-2 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold"
               data-testid="brief-public-linkedin">in LinkedIn</a>
            <button onClick={copyLink}
                    className="px-4 py-2 rounded-full bg-white text-zinc-900 hover:bg-zinc-100 text-sm font-semibold"
                    data-testid="brief-public-copy">
              {copied ? "✓ Copied" : "🔗 Copy link"}
            </button>
          </div>
        </div>

        {/* CTA back to app */}
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-6 text-white text-center shadow-xl">
          <h2 className="text-xl font-bold mb-2">Find your next SaaS in 60 seconds.</h2>
          <p className="text-violet-100 text-sm mb-4">Prime Pillar surfaces real complaints from real paying customers, daily.</p>
          <button onClick={onEnterApp}
                  className="px-6 py-3 bg-white text-violet-700 font-bold rounded-xl hover:bg-zinc-50"
                  data-testid="brief-public-enter-app">
            Explore opportunities →
          </button>
        </div>
      </div>
    </div>
  );
};

export default BriefPublicView;
